import React, { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../../components/Layout';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    const msg = Array.isArray(err.message) ? err.message.join('; ') : err.message;
    const e = new Error(msg || 'Request failed') as Error & { status?: number };
    e.status = res.status;
    throw e;
  }
  if (res.status === 204) return null;
  return res.json();
}

const PRIORITY = {
  production_stopped: { label: 'Production stopped', cls: 'bg-red-100 text-red-800 border border-red-300' },
  high: { label: 'High', cls: 'bg-amber-100 text-amber-800 border border-amber-300' },
  normal: { label: 'Normal', cls: 'bg-gray-100 text-gray-700 border border-gray-300' },
} as const;
const STATUSES = ['new', 'assigned', 'in_progress', 'waiting', 'resolved', 'closed'];
const STATUS_LABEL: Record<string, string> = { new: 'New', assigned: 'Assigned', in_progress: 'In progress', waiting: 'Waiting', resolved: 'Resolved', closed: 'Closed' };
const RESOLUTION_CATEGORIES = [
  { value: 'software', label: 'Software' },
  { value: 'order_data', label: 'Order or data' },
  { value: 'workstation_equipment', label: 'Workstation or equipment' },
  { value: 'process', label: 'Process' },
  { value: 'training', label: 'Training' },
  { value: 'sop', label: 'SOP' },
  { value: 'improvement', label: 'Improvement idea' },
  { value: 'no_action', label: 'No action needed' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'other', label: 'Other' },
];
const fmt = (d: any) => (d ? new Date(d).toLocaleString() : '—');

function PriorityBadge({ p }: { p: string }) {
  const x = (PRIORITY as any)[p] || PRIORITY.normal;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${x.cls}`}>{x.label}</span>;
}
function StatusBadge({ s }: { s: string }) {
  const done = s === 'resolved' || s === 'closed';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${done ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>{STATUS_LABEL[s] || s}</span>;
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-start justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full my-8">
          <div className="border-b px-6 py-4 flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

const FU_STATUSES = ['open', 'in_progress', 'done', 'cancelled'];
const FU_LABEL: Record<string, string> = { open: 'Open', in_progress: 'In progress', done: 'Done', cancelled: 'Cancelled' };
const fuOverdue = (f: any) => f.dueDate && !['done', 'cancelled'].includes(f.status) && new Date(String(f.dueDate).slice(0, 10) + 'T23:59:59') < new Date();
const SourceChip = ({ s }: { s: string }) => (
  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${s === 'office' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`}>{s === 'office' ? 'Office' : 'Floor'}</span>
);

// One home for everything anyone needs from you: floor reports + things office
// staff add, plus a Follow-ups tab to track that it actually got done.
const TERMINAL = ['resolved', 'closed'];

export function SupportRequestsPage() {
  const [tab, setTab] = useState<'requests' | 'followups' | 'resolved'>('requests');
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [followUps, setFollowUps] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [addKind, setAddKind] = useState<'' | 'request' | 'followup'>('');

  const load = async () => {
    setLoading(true); setLoadError(''); setForbidden(false);
    try {
      const [list, fus, emps] = await Promise.all([
        apiRequest('/support-requests'),
        apiRequest('/follow-ups').catch(() => []),
        apiRequest('/employees?status=active').catch(() => []),
      ]);
      setRows(list || []);
      setFollowUps(fus || []);
      setEmployees((emps || []).map((e: any) => ({ id: e.id, name: e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim() })));
    } catch (e: any) {
      if (e.status === 403) setForbidden(true); else setLoadError(e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setFuStatus = async (id: string, status: string) => {
    const closeNote = ['done', 'cancelled'].includes(status) ? (window.prompt('Add a closing note (optional):') || '') : undefined;
    try { await apiRequest(`/follow-ups/${id}`, { method: 'PATCH', body: JSON.stringify({ status, ...(closeNote ? { closeNote } : {}) }) }); await load(); }
    catch (e: any) { setLoadError(e.message); }
  };
  const del = async (r: any) => {
    if (!window.confirm(`Delete ${r.requestNumber}? This removes it for good.`)) return;
    try { await apiRequest(`/support-requests/${r.id}`, { method: 'DELETE' }); await load(); }
    catch (e: any) { setLoadError(e.message); }
  };

  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  if (forbidden) return (
    <>
      <PageHeader title="Support Requests" subtitle="Problems and to-dos" />
      <Card><div className="text-center py-10">
        <div className="text-lg font-semibold text-gray-900">Factory Admin access required</div>
        <div className="text-sm text-gray-600 mt-2 max-w-md mx-auto">This is limited to Factory Admin accounts.</div>
      </div></Card>
    </>
  );

  const activeRows = rows.filter(r => !TERMINAL.includes(r.status));
  const resolvedRows = rows.filter(r => TERMINAL.includes(r.status));

  const TabBtn = ({ id, label, count }: { id: 'requests' | 'followups' | 'resolved'; label: string; count?: number }) => (
    <button onClick={() => setTab(id)}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === id ? 'border-gray-800 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      {label}{count ? <span className="ml-1.5 text-xs text-gray-400">({count})</span> : null}
    </button>
  );

  const RequestsTable = ({ list, empty }: { list: any[]; empty: string }) => (
    list.length === 0 ? <p className="text-gray-500 text-center py-10">{empty}</p> : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b"><tr className="text-left text-sm text-gray-600">
            <th className="px-4 py-3 font-medium">#</th>
            <th className="px-4 py-3 font-medium">Priority</th>
            <th className="px-4 py-3 font-medium">From</th>
            <th className="px-4 py-3 font-medium">Reported by</th>
            <th className="px-4 py-3 font-medium">What's wrong / needed</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Assigned</th>
            <th className="px-4 py-3 font-medium">When</th>
            <th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr></thead>
          <tbody className="divide-y">
            {list.map(r => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.requestNumber}</td>
                <td className="px-4 py-3"><PriorityBadge p={r.priority} /></td>
                <td className="px-4 py-3"><SourceChip s={r.source} /></td>
                <td className="px-4 py-3 text-sm text-gray-700">{r.employeeName}</td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{r.description}</td>
                <td className="px-4 py-3"><StatusBadge s={r.status} /></td>
                <td className="px-4 py-3 text-sm text-gray-600">{r.assignedToName || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{fmt(r.createdAt)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setOpenId(r.id)} className="text-blue-600 hover:text-blue-700 font-medium text-sm">Open</button>
                  <button onClick={() => del(r)} className="ml-3 text-red-600 hover:text-red-700 text-sm">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  );

  return (
    <>
      <PageHeader
        title="Support Requests"
        subtitle="Everything anyone needs from you, in one place. Add it here instead of coming to the office."
        actions={
          tab === 'followups'
            ? <button onClick={() => setAddKind('followup')} className="px-3 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900">+ Add follow-up</button>
            : <div className="flex gap-2">
                <button onClick={() => setAddKind('request')} className="px-3 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900">+ Add request</button>
                <button onClick={() => setAddKind('followup')} className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">+ Add follow-up</button>
              </div>
        }
      />
      <div className="border-b border-gray-200 mb-4 flex gap-2">
        <TabBtn id="requests" label="Requests" count={activeRows.length} />
        <TabBtn id="followups" label="Follow-ups" count={followUps.filter(f => !['done', 'cancelled'].includes(f.status)).length} />
        <TabBtn id="resolved" label="Resolved" count={resolvedRows.length} />
      </div>
      {loadError && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{loadError}</div>}

      {tab === 'requests' ? (
        <Card><RequestsTable list={activeRows} empty="No open requests." /></Card>
      ) : tab === 'resolved' ? (
        <Card><RequestsTable list={resolvedRows} empty="Nothing resolved or closed yet." /></Card>
      ) : (
        <Card>
          {followUps.length === 0 ? <p className="text-gray-500 text-center py-10">No follow-ups yet.</p> : (
            <div className="space-y-3">
              {followUps.map(f => {
                const done = ['done', 'cancelled'].includes(f.status);
                return (
                  <div key={f.id} className={`rounded-lg border p-4 ${fuOverdue(f) ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className={`font-medium text-gray-900 ${done ? 'line-through text-gray-400' : ''}`}>{f.title}</div>
                        {f.description && <div className="text-sm text-gray-600 mt-0.5 whitespace-pre-wrap">{f.description}</div>}
                        <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span>Owner: {f.ownerName || '—'}</span>
                          <span className={fuOverdue(f) ? 'text-red-600 font-semibold' : ''}>Due {f.dueDate ? fmt(f.dueDate).split(',')[0] : '—'}{fuOverdue(f) ? ' — overdue' : ''}</span>
                          {f.sourceRequestNumber && <span>From {f.sourceRequestNumber}</span>}
                          {f.subjectName && <span>About {f.subjectName}</span>}
                        </div>
                        {f.closeNote && <div className="text-xs text-gray-500 mt-1 italic">Closed: {f.closeNote}</div>}
                      </div>
                      <select value={f.status} onChange={e => setFuStatus(f.id, e.target.value)} className="shrink-0 px-2 py-1 border border-gray-300 rounded-lg text-sm">
                        {FU_STATUSES.map(s => <option key={s} value={s}>{FU_LABEL[s]}</option>)}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {openId && <DetailModal id={openId} employees={employees} onClose={() => { setOpenId(null); load(); }} onChanged={load} />}
      {addKind === 'request' && <AddRequestModal onClose={() => setAddKind('')} onDone={() => { setAddKind(''); setTab('requests'); load(); }} />}
      {addKind === 'followup' && <AddFollowUpModal employees={employees} onClose={() => setAddKind('')} onDone={() => { setAddKind(''); setTab('followups'); load(); }} />}
    </>
  );
}

function AddRequestModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';
  const save = async () => {
    if (!description.trim()) { setError('Describe what you need.'); return; }
    setSaving(true); setError('');
    try { await apiRequest('/support-requests/office', { method: 'POST', body: JSON.stringify({ description: description.trim(), priority }) }); onDone(); }
    catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };
  return (
    <Modal title="Add a request" onClose={onClose}>
      <div className="space-y-3">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">What's the problem / what do you need?</label>
          <textarea rows={3} className={input} value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Label printer at packing is out of ribbon" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select className={input} value={priority} onChange={e => setPriority(e.target.value)}>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="production_stopped">Production stopped</option>
          </select>
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50">{saving ? 'Adding…' : 'Add request'}</button>
        </div>
      </div>
    </Modal>
  );
}

function AddFollowUpModal({ employees, onClose, onDone }: { employees: any[]; onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';
  const save = async () => {
    if (!title.trim()) { setError('Give it a title.'); return; }
    setSaving(true); setError('');
    try { await apiRequest('/follow-ups', { method: 'POST', body: JSON.stringify({ title: title.trim(), ownerId: ownerId || undefined, dueDate: dueDate || undefined }) }); onDone(); }
    catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };
  return (
    <Modal title="Add a follow-up" onClose={onClose}>
      <div className="space-y-3">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">What needs doing</label>
          <input className={input} value={title} onChange={e => setTitle(e.target.value)} placeholder='e.g. "Order a replacement jig for WB-ASSEMBLY"' />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
            <select className={input} value={ownerId} onChange={e => setOwnerId(e.target.value)}><option value="">Unassigned</option>{employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Due date</label>
            <input type="date" className={input} value={dueDate} onChange={e => setDueDate(e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50">{saving ? 'Adding…' : 'Add follow-up'}</button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><div className="text-xs uppercase tracking-wide text-gray-400 mb-0.5">{label}</div><div className="text-sm text-gray-900">{children}</div></div>;
}

function DetailModal({ id, employees, onClose, onChanged }: { id: string; employees: any[]; onClose: () => void; onChanged: () => void }) {
  const [r, setR] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [priorityReason, setPriorityReason] = useState('');
  const [resCategory, setResCategory] = useState('');
  const [resNote, setResNote] = useState('');
  const [dupOf, setDupOf] = useState('');
  const [misNote, setMisNote] = useState('');
  const [followUps, setFollowUps] = useState<any[]>([]);
  const [showFu, setShowFu] = useState(false);
  const [fuTitle, setFuTitle] = useState('');
  const [fuOwner, setFuOwner] = useState('');
  const [fuDue, setFuDue] = useState('');
  const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';

  const reload = async () => { try { setR(await apiRequest(`/support-requests/${id}`)); } catch (e: any) { setError(e.message); } };
  const loadFollowUps = async () => { try { setFollowUps(await apiRequest(`/follow-ups?sourceType=support_request&sourceId=${id}`) || []); } catch { /* leave */ } };
  useEffect(() => { reload(); loadFollowUps(); }, [id]);
  if (!r) return <Modal title="Request" onClose={onClose}><div className="py-10 flex justify-center"><LoadingSpinner /></div></Modal>;

  const done = r.status === 'resolved' || r.status === 'closed';
  const act = async (key: string, fn: () => Promise<any>) => {
    setBusy(key); setError('');
    try { await fn(); await reload(); onChanged(); } catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };

  return (
    <Modal title={`${r.requestNumber} — Support request`} onClose={onClose}>
      <div className="space-y-5">
        <div className="flex items-center gap-3 flex-wrap">
          <PriorityBadge p={r.priority} />
          <StatusBadge s={r.status} />
          {r.derivedPriority !== r.priority && <span className="text-xs text-gray-500">(derived: {(PRIORITY as any)[r.derivedPriority]?.label})</span>}
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">{error}</div>}

        {/* What the employee reported — untouched */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Field label="Reported by">{r.employeeName}</Field>
          <Field label="Can keep working">{r.canKeepWorking ? 'Yes' : 'No'}</Field>
          <Field label="Order stopped">{r.currentOrderStopped ? 'Yes' : 'No'}</Field>
          <Field label="When">{fmt(r.createdAt)}</Field>
          <Field label="Workstation">{r.workstationName || '—'}</Field>
          <Field label="Order">{r.orderNumber || '—'}</Field>
          <Field label="Photo">{r.hasPhoto ? <a className="text-blue-600 hover:underline" href="#" onClick={(e) => { e.preventDefault(); openPhoto(id); }}>View</a> : '—'}</Field>
        </div>
        <Field label="What's wrong (their words)"><div className="whitespace-pre-wrap bg-gray-50 border rounded p-3">{r.description}</div></Field>

        {/* Assign + status */}
        {!done && (
          <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Assign to</label>
              <div className="flex gap-2">
                <select value={assignTo || r.assignedTo || ''} onChange={e => setAssignTo(e.target.value)} className={input}>
                  <option value="">—</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <button disabled={!assignTo || !!busy} onClick={() => act('assign', () => apiRequest(`/support-requests/${id}/assign`, { method: 'PATCH', body: JSON.stringify({ assignedTo: assignTo }) }))}
                  className="px-3 py-2 bg-gray-800 text-white rounded-lg text-sm disabled:opacity-50">Assign</button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select value={r.status} onChange={e => act('status', () => apiRequest(`/support-requests/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status: e.target.value }) }))} className={input}>
                {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Priority change with reason + history */}
        <div className="border-t pt-4">
          <div className="text-sm font-semibold text-gray-700 mb-2">Priority</div>
          {(r.priorityHistory || []).length > 0 && (
            <ul className="text-sm text-gray-600 mb-3 space-y-1">
              {r.priorityHistory.map((h: any, i: number) => (
                <li key={i}><span className="line-through text-gray-400">{(PRIORITY as any)[h.from]?.label || h.from}</span> → <span className="font-medium">{(PRIORITY as any)[h.to]?.label || h.to}</span> — {h.reason} <span className="text-xs text-gray-400">({h.changedByName}, {fmt(h.at)})</span></li>
              ))}
            </ul>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select value={newPriority} onChange={e => setNewPriority(e.target.value)} className={input}>
              <option value="">Change priority to…</option>
              {Object.keys(PRIORITY).map(p => <option key={p} value={p}>{(PRIORITY as any)[p].label}</option>)}
            </select>
            <input className={`${input} sm:col-span-1`} placeholder="Reason for the change" value={priorityReason} onChange={e => setPriorityReason(e.target.value)} />
            <button disabled={!newPriority || !priorityReason.trim() || !!busy}
              onClick={() => act('priority', async () => { await apiRequest(`/support-requests/${id}/priority`, { method: 'PATCH', body: JSON.stringify({ priority: newPriority, reason: priorityReason.trim() }) }); setNewPriority(''); setPriorityReason(''); })}
              className="px-3 py-2 bg-gray-800 text-white rounded-lg text-sm disabled:opacity-50">Change priority</button>
          </div>
        </div>

        {/* Resolve — category + note kept separately from the employee's report */}
        {!done ? (
          <div className="border-t pt-4">
            <div className="text-sm font-semibold text-gray-700 mb-2">Resolve</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              <select value={resCategory} onChange={e => setResCategory(e.target.value)} className={input}>
                <option value="">What it actually was…</option>
                {RESOLUTION_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {resCategory === 'duplicate' && (
                <input className={input} placeholder="Duplicate of (request UUID)" value={dupOf} onChange={e => setDupOf(e.target.value)} />
              )}
            </div>
            <textarea rows={2} className={input} placeholder="Resolution note (this is what the employee sees)" value={resNote} onChange={e => setResNote(e.target.value)} />
            <div className="flex justify-end mt-2">
              <button disabled={!resCategory || !resNote.trim() || !!busy}
                onClick={() => act('resolve', () => apiRequest(`/support-requests/${id}/resolve`, { method: 'POST', body: JSON.stringify({ resolutionCategory: resCategory, resolutionNote: resNote.trim(), duplicateOf: dupOf || undefined }) }))}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">{busy === 'resolve' ? 'Saving…' : 'Resolve'}</button>
            </div>
          </div>
        ) : (
          <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="What it actually was">{RESOLUTION_CATEGORIES.find(c => c.value === r.resolutionCategory)?.label || '—'}</Field>
            <Field label="Resolution note (employee sees this)"><div className="whitespace-pre-wrap">{r.resolutionNote}</div></Field>
            {r.duplicateOfNumber && <Field label="Duplicate of">{r.duplicateOfNumber}</Field>}
          </div>
        )}

        {/* The one flag that needs care */}
        <div className="border-t pt-4">
          <div className="text-sm font-semibold text-gray-700 mb-1">Reported as production stopped — was not</div>
          <p className="text-xs text-gray-500 mb-2">A fact about this request, not a mark against the person. Never counted, never shown to the employee.</p>
          {r.misreportedStopped ? (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
              <span className="font-medium text-amber-900">Flagged.</span> {r.misreportedNote}
              <button disabled={!!busy} onClick={() => act('mis', () => apiRequest(`/support-requests/${id}/misreported`, { method: 'PATCH', body: JSON.stringify({ misreported: false }) }))}
                className="ml-3 text-xs text-gray-500 hover:text-gray-700 underline">Remove flag</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input className={`${input} sm:col-span-2`} placeholder="What was actually happening" value={misNote} onChange={e => setMisNote(e.target.value)} />
              <button disabled={!misNote.trim() || !!busy}
                onClick={() => act('mis', async () => { await apiRequest(`/support-requests/${id}/misreported`, { method: 'PATCH', body: JSON.stringify({ misreported: true, note: misNote.trim() }) }); setMisNote(''); })}
                className="px-3 py-2 bg-white border border-amber-400 text-amber-800 rounded-lg text-sm disabled:opacity-50">Flag it</button>
            </div>
          )}
        </div>

        {/* Follow-ups — something someone has to DO as a result. Separate from the
            request: closing one doesn't close the other. */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-gray-700">Follow-ups</div>
            {!showFu && <button onClick={() => { setShowFu(true); setFuTitle(`Follow up: ${r.description?.slice(0, 60) || ''}`); }} className="text-sm text-blue-600 hover:text-blue-700 font-medium">+ Create follow-up</button>}
          </div>
          {followUps.length === 0 && !showFu && <p className="text-sm text-gray-400">None yet.</p>}
          {followUps.length > 0 && (
            <ul className="space-y-1 mb-3">
              {followUps.map(f => (
                <li key={f.id} className="text-sm text-gray-700 flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${['done','cancelled'].includes(f.status) ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700'}`}>{f.status.replace('_',' ')}</span>
                  <span className={['done','cancelled'].includes(f.status) ? 'line-through text-gray-400' : ''}>{f.title}</span>
                  {f.ownerName && <span className="text-xs text-gray-400">→ {f.ownerName}</span>}
                  {f.dueDate && <span className="text-xs text-gray-400">· due {fmt(f.dueDate).split(',')[0]}</span>}
                </li>
              ))}
            </ul>
          )}
          {showFu && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
              <input className={input} value={fuTitle} onChange={e => setFuTitle(e.target.value)} placeholder="What needs doing" />
              <div className="grid grid-cols-2 gap-2">
                <select className={input} value={fuOwner} onChange={e => setFuOwner(e.target.value)}><option value="">Owner…</option>{employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}</select>
                <input type="date" className={input} value={fuDue} onChange={e => setFuDue(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setShowFu(false); setFuTitle(''); setFuOwner(''); setFuDue(''); }} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button disabled={!fuTitle.trim() || !!busy}
                  onClick={() => act('fu', async () => {
                    await apiRequest('/follow-ups', { method: 'POST', body: JSON.stringify({ title: fuTitle.trim(), ownerId: fuOwner || undefined, dueDate: fuDue || undefined, sourceType: 'support_request', sourceId: id, subjectEmployeeId: r.employeeId || undefined }) });
                    setShowFu(false); setFuTitle(''); setFuOwner(''); setFuDue(''); await loadFollowUps();
                  })}
                  className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-lg disabled:opacity-50">{busy === 'fu' ? 'Creating…' : 'Create follow-up'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function openPhoto(id: string) {
  const token = localStorage.getItem('admin_auth_token');
  fetch(`${API_BASE}/support-requests/${id}/photo`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
    .then(b => { const url = URL.createObjectURL(b); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60_000); })
    .catch(() => alert("Couldn't open the photo."));
}

export default SupportRequestsPage;
